import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core'
import { Delete as DeleteIcon } from '@material-ui/icons'
import EditProjectModal from './EditProjectModal'
import SummaryCards from './SummaryCards'
import SectionBreakdown from './SectionBreakdown'
import ActionItems from './ActionItems'
import RecentActivity from './RecentActivity'
import TaskPanel from './TaskPanel/TaskPanel'
import NotificationBell from './NotificationBell'
import NotificationDrawer from './NotificationDrawer/NotificationDrawer'
import ContextDrawer from './ContextDrawer/ContextDrawer'
import UploadDropzone from './UploadDropzone'
import DocumentsSection from '../Documents/DocumentsSection'
import ModulesSection from './ModulesSection/ModulesSection'
import FollowUpPanel from './FollowUpPanel'
import SubmissionReadinessModal from './SubmissionReadinessModal'
import WorkflowStepper from './WorkflowStepper'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const statusChipProps = (status) => {
  const map = {
    draft:      { label: 'Draft',      className: 'pill-warn' },
    estimating: { label: 'Estimating', className: 'pill-info' },
    review:     { label: 'Review',     className: 'pill-accent' },
    submitted:  { label: 'Submitted',  className: 'pill-brand' },
    won:        { label: 'Won',        className: 'pill-ok' },
    lost:       { label: 'Lost',       className: 'pill-risk' }
  }
  return map[status] || { label: status || 'Draft', className: 'pill-neutral' }
}


const ProjectDashboard = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [dashboard, setDashboard] = React.useState(null)
  const [activity, setActivity] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [bidPackages, setBidPackages] = React.useState([])
  const [deleteTarget, setDeleteTarget] = React.useState(null)
  const [readinessOpen, setReadinessOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [notifDrawerOpen, setNotifDrawerOpen] = React.useState(false)
  const [notifCount, setNotifCount] = React.useState(0)
  const [contextDrawerOpen, setContextDrawerOpen] = React.useState(false)
  const [contextInitialTab, setContextInitialTab] = React.useState('attention')
  const [contextSummary, setContextSummary] = React.useState(null)
  const uploadRef = React.useRef(null)
  const [editProjectOpen, setEditProjectOpen] = React.useState(false)
  const [recalculating, setRecalculating] = React.useState(false)

  React.useEffect(() => {
    fetchDashboard()
    fetchActivity()
    fetchBidPackages()
    fetchContextSummary()
  }, [projectId])

  const fetchBidPackages = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/bid-packages`).then(res => {
      setBidPackages(res.data)
    }).catch(err => {
      console.error('Failed to fetch bid packages:', err)
    })
  }

  const handleDeletePackage = () => {
    if (!deleteTarget) return
    setDeleting(true)
    axios.delete(`${props.url}/api/bid-estimation/bid-packages/${deleteTarget._id}`)
      .then(() => {
        setDeleting(false)
        setDeleteTarget(null)
        fetchBidPackages()
      })
      .catch(err => {
        setDeleting(false)
        console.error('Failed to delete bid package:', err)
        alert('Failed to delete: ' + (err.response?.data?.message || err.message))
      })
  }

  const fetchDashboard = () => {
    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/dashboard`).then(res => {
      setDashboard(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const fetchActivity = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/activity`).then(res => {
      setActivity(res.data)
    }).catch(err => {
      console.error('Failed to fetch activity:', err)
    })
  }

  const fetchContextSummary = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/context/summary`).then(res => {
      setContextSummary(res.data)
    }).catch(err => {
      console.error('Failed to fetch context summary:', err)
    })
  }

  const handleRecalculateAll = async () => {
    setRecalculating(true)
    try {
      await axios.post(`${props.url}/api/bid-estimation/projects/${projectId}/project-services/recalculate-all`)
      fetchDashboard()
    } catch (err) {
      console.error('Failed to recalculate services:', err)
      alert('Failed to recalculate services: ' + (err.response?.data?.error || err.message))
    } finally {
      setRecalculating(false)
    }
  }

  if (loading || !dashboard) {
    return <div className="hmi-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}><CircularProgress /></div>
  }

  const { project, summary, serviceEstimate, byBuilding, bySection, vendorQuoteSummary } = dashboard
  const chipProps = statusChipProps(project?.status)
  const firstBidPackage = bidPackages[0]

  const handleWorkflowStepClick = (step) => {
    switch (step) {
      case 'upload':
        uploadRef.current?.triggerUpload()
        break
      case 'scope':
      case 'link':
        history.push(`/bid-estimation/${projectId}/scope`)
        break
      case 'estimate':
        history.push(`/bid-estimation/${projectId}/estimating`)
        break
      case 'output':
        history.push(`/bid-estimation/${projectId}/output`)
        break
      default:
        break
    }
  }

  const workflowGuide = {
    upload: {
      label: 'Next Step',
      title: 'Upload project sources',
      body: 'Add a bid schedule, plan set, vendor quote, or manually enter line items to start building scope.',
      primaryLabel: 'Upload Document',
      primaryAction: () => uploadRef.current?.triggerUpload(),
      secondaryLabel: 'Manual Line Items',
      secondaryAction: () => history.push(`/bid-estimation/${projectId}/items`)
    },
    scope: {
      label: 'Next Step',
      title: 'Assemble source scope',
      body: firstBidPackage
        ? 'Review the parsed bid schedule, then reconcile schedule rows, plan takeoffs, and quote evidence into Assembly Items.'
        : 'Open the Assembly Workbench to reconcile source evidence into Assembly Items.',
      primaryLabel: 'Open Assembly Workbench',
      primaryAction: () => history.push(`/bid-estimation/${projectId}/scope`),
      secondaryLabel: firstBidPackage ? 'Review Bid Schedule' : null,
      secondaryAction: firstBidPackage
        ? () => history.push(`/bid-estimation/${projectId}/bid-response/${firstBidPackage._id}`)
        : null
    },
    link: {
      label: 'Next Step',
      title: 'Link services and pricing',
      body: 'Assign service templates, connect materials, and apply vendor quote pricing before estimate review.',
      primaryLabel: 'Open Assembly Workbench',
      primaryAction: () => history.push(`/bid-estimation/${projectId}/scope`),
      secondaryLabel: 'Vendor Quotes',
      secondaryAction: () => history.push(`/bid-estimation/${projectId}/vendor-quotes`)
    },
    estimate: {
      label: 'Next Step',
      title: 'Review estimate',
      body: 'Check calculated costs, margin, coverage, and any remaining pricing exceptions.',
      primaryLabel: 'Open Estimating',
      primaryAction: () => history.push(`/bid-estimation/${projectId}/estimating`)
    },
    output: {
      label: 'Next Step',
      title: 'Prepare bid output',
      body: 'Preview the final bid response and generate the client-facing output package.',
      primaryLabel: 'Preview Output',
      primaryAction: () => history.push(`/bid-estimation/${projectId}/output`)
    }
  }[dashboard.workflowStep] || null

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Projects', to: '/bid-estimation' },
          { label: project?.name || 'Project' }
        ]}
        title={project?.name || 'Project Dashboard'}
        statusChip={chipProps}
        meta={[
          project?.bidNumber && `P-${project.bidNumber}`,
          project?.client,
          project?.bidDueDate && `Bid due ${new Date(project.bidDueDate).toLocaleDateString()}`,
          project?.teamMode ? 'Team Project' : null
        ].filter(Boolean).join(' \u00b7 ') || null}
        actions={
          <>
            <button className="hmi-btn hmi-btn-ghost" onClick={() => setEditProjectOpen(true)}>Settings</button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => history.push(`/bid-estimation/${projectId}/items`)}>Line Items</button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => history.push(`/bid-estimation/${projectId}/scope`)}>Assembly Workbench</button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => history.push(`/bid-estimation/${projectId}/estimating`)}>Estimating</button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => history.push(`/bid-estimation/${projectId}/vendor-quotes`)}>Vendor Quotes</button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => history.push(`/bid-estimation/${projectId}/output`)}>Output</button>
            <button className="hmi-btn hmi-btn-outline" onClick={() => { setContextInitialTab('attention'); setContextDrawerOpen(true) }}>Context</button>
            <NotificationBell onOpen={() => setNotifDrawerOpen(true)} unreadCount={notifCount} onCountChange={setNotifCount} />
            <UploadDropzone
              ref={uploadRef}
              projectId={projectId}
              url={props.url}
              onUploadComplete={(result) => {
                fetchActivity()
                if (result?.action === 'open_quote_review' && result?.vendorQuote?._id) {
                  history.push(`/bid-estimation/${projectId}/vendor-quotes`)
                }
                if (result?.action === 'open_bid_response' && result?.bidPackageId) {
                  history.push(`/bid-estimation/${projectId}/bid-response/${result.bidPackageId}`)
                }
              }}
            />
          </>
        }
      />

      {/* ── Content Area ────────────────────────────────────────────── */}
      <div className="hmi-body">

        {/* Workflow Stepper */}
        {dashboard.workflowStep && (
          <WorkflowStepper
            workflowStep={dashboard.workflowStep}
            scopeProgress={dashboard.scopeProgress}
            onStepClick={handleWorkflowStepClick}
          />
        )}

        {workflowGuide && (
          <div
            className="hmi-panel"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '14px 18px',
              marginBottom: 20
            }}
          >
            <div>
              <div style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                {workflowGuide.label}
              </div>
              <div style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                {workflowGuide.title}
              </div>
              <div style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.45 }}>
                {workflowGuide.body}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {workflowGuide.secondaryLabel && (
                <button className="hmi-btn hmi-btn-outline" onClick={workflowGuide.secondaryAction}>
                  {workflowGuide.secondaryLabel}
                </button>
              )}
              <button className="hmi-btn hmi-btn-primary" onClick={workflowGuide.primaryAction}>
                {workflowGuide.primaryLabel}
              </button>
            </div>
          </div>
        )}

        {/* KPI + Attention row — stats on top-left, panels below, attention on right */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 20 }}>
          {/* Left column: stats + documents/tasks/follow-up */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SummaryCards summary={summary} serviceEstimate={serviceEstimate} />

            {/* Getting started hint */}
            {dashboard && summary && summary.totalItems === 0 && bidPackages.length === 0 && (!project?.documents || project.documents.length === 0) && (
              <div style={{
                backgroundColor: 'var(--info-tint)',
                borderRadius: 8,
                padding: 16,
                border: '1px solid oklch(0.88 0.04 230)'
              }}>
                <div style={{ color: 'var(--info)', fontWeight: 600, marginBottom: 4, fontSize: 14 }}>Getting Started</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                  Upload plans or a bid response to auto-populate line items, or go to{' '}
                  <span style={{ color: 'var(--info)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => history.push(`/bid-estimation/${projectId}/items`)}>Line Items</span>
                  {' '}to manually build your scope of work.
                </div>
              </div>
            )}

            {/* Documents + Tasks + Follow-Up in the space below stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <DocumentsSection
                  projectId={projectId}
                  project={project}
                  url={props.url}
                  onRefresh={fetchDashboard}
                  onTriggerUpload={() => uploadRef.current?.triggerUpload()}
                  bidPackages={bidPackages}
                  onDeleteBidPackage={setDeleteTarget}
                  onOpenContextDrawer={(filter) => { setContextInitialTab('attention'); setContextDrawerOpen(true) }}
                />
                {/* Bid Response Packages now shown inline in DocumentsSection */}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <TaskPanel projectId={projectId} />
                <FollowUpPanel projectId={projectId} project={project} onRefresh={fetchDashboard} />
              </div>
            </div>
          </div>

          {/* Right column: attention panel + CTAs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ActionItems
              projectId={projectId}
              summary={summary}
              scopeProgress={dashboard.scopeProgress}
              bySection={bySection}
              vendorQuoteSummary={vendorQuoteSummary}
              contextSummary={contextSummary}
              onOpenContextDrawer={(tab) => { setContextInitialTab(tab); setContextDrawerOpen(true) }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="hmi-btn hmi-btn-primary" style={{ flex: 1 }} onClick={handleRecalculateAll} disabled={recalculating}>
                {recalculating ? 'Recalculating...' : 'Recalculate all'}
              </button>
              <button className="hmi-btn hmi-btn-outline" style={{ flex: 1 }} onClick={() => history.push(`/bid-estimation/${projectId}/output`)}>Preview output</button>
            </div>
          </div>
        </div>

        {/* Section Breakdown — full width */}
        <div className="hmi-panel" style={{ marginBottom: 20 }}>
          <SectionBreakdown bySection={bySection} byBuilding={byBuilding} selectedBuilding={null} />
        </div>

        {/* Modules + Activity */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <ModulesSection projectId={projectId} project={project} onOpenContextDrawer={(filter) => { setContextInitialTab('attention'); setContextDrawerOpen(true) }} />
          <RecentActivity activity={activity} />
        </div>

      </div>

      {/* Notification Drawer */}
      <NotificationDrawer
        open={notifDrawerOpen}
        onClose={() => setNotifDrawerOpen(false)}
        onCountChange={setNotifCount}
      />

      {/* Context Drawer */}
      <ContextDrawer
        open={contextDrawerOpen}
        onClose={() => { setContextDrawerOpen(false); fetchContextSummary() }}
        projectId={projectId}
        initialTab={contextInitialTab}
      />

      {/* Submission Readiness */}
      <SubmissionReadinessModal
        open={readinessOpen}
        projectId={projectId}
        url={props.url}
        onClose={() => setReadinessOpen(false)}
        onSubmitted={() => { setReadinessOpen(false); fetchDashboard() }}
      />

      {/* Delete Package Confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Delete Bid Package?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete <strong>{deleteTarget && deleteTarget.name}</strong>, all its groups, items, and mappings. The source file will also be removed from S3.
          </DialogContentText>
          <DialogContentText style={{ marginTop: 8 }}>
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeletePackage}
            color='secondary'
            variant='contained'
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <EditProjectModal
        open={editProjectOpen}
        onClose={() => setEditProjectOpen(false)}
        project={project}
        url={props.url}
        onSaved={(updated) => {
          setDashboard(prev => prev ? { ...prev, project: { ...prev.project, ...updated } } : prev)
        }}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated,
  users: state.users || []
})

export default connect(mapStateToProps)(ProjectDashboard)
